import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect } from 'react';

import useLoggedUser from '../../../../../hooks/use-logged-user';
import usePageViewEvent from '../../../../../hooks/use-page-view-event';
import useHreflang from '../../../../../utils/seo/hreflang';
import useOGMeta from '../../../../../utils/seo/ogmeta';
import useSchema from '../../../../../utils/seo/schema-org/schema';
import { useAppContext } from '../../../../providers/app-context';

const SubCategoriesSchema: FunctionComponent<{
  info: any;
  breadcrumbs: Array<{
    href: LinkProps;
    text: string;
  }>;
  translatedSlugs: any;
  count: number;
}> = ({ info, breadcrumbs, translatedSlugs, count }) => {
  const { businessValues } = useAppContext();
  const { locale = 'en', asPath, pathname, query } = useRouter();
  const { t } = useTranslation();
  const localePath = locale !== 'en' ? `/${locale}` : '';

  const name = t('sub-categories:head.title', {
    categoryPlural: info?.slug
      ? t(`parts-and-categories-plural:categories.${info?.slug}.plural`)
      : info.name,
  });

  const description = t('sub-categories:head.description', {
    categoryPlural: info?.slug
      ? t(`parts-and-categories-plural:categories.${info?.slug}.plural`)
      : info.name,
    category: info?.slug
      ? t(`parts-and-categories-plural:categories.${info?.slug}.singular`)
      : info.name,
    count: count.toLocaleString(locale),
    usedWord:
      info?.gender && info?.gender === 'F'
        ? t('grammar:words.used.feminine')
        : t('grammar:words.used.masculine'),
    yoursPlural:
      info?.gender && info?.gender === 'F'
        ? t('grammar:pronnouns.yoursPlural.feminine')
        : t('grammar:pronnouns.yoursPlural.masculine'),
    totalStockAvailable: businessValues.totalStockAvailable.defaultValue,
  });

  const og = useOGMeta({
    title: name,
    description: description,
    url: process.env.NEXT_PUBLIC_HOST + localePath + asPath,
  });

  const currentRoute = (() => {
    if (pathname.includes('[category]')) {
      if (pathname.includes('[slug]')) {
        // sub-categories-with-vehicle: /auto-parts/[category]/category/[slug]
        return 'sub-categories-with-vehicle';
      }

      if (pathname.includes('categories')) {
        // categories-with-vehicle: /auto-parts/[category]/categories
        return 'categories-with-vehicle';
      }

      // sub-categories: /auto-parts/[category]
      return 'sub-categories';
    }

    // categories: /auto-parts/categories
    return 'categories';
  })();

  const alternateLinks = useHreflang({
    locale,
    route: currentRoute,
    params: query,
    translatedSlugs: translatedSlugs?.slugs,
  });

  usePageViewEvent(name);

  return (
    <Head>
      <title>{name}</title>
      <meta name="description" key="description" content={description} />
      {alternateLinks}
      {og}

      <script {...useSchema({ name, description, breadcrumbs })} />
    </Head>
  );
};

export default SubCategoriesSchema;
